import { useContext, useEffect } from 'react'

import {
  SanityPrintAreaType,
  SanityPrintPermission,
  SanityPrintSlotSettings,
} from '@data/sanity/queries/types/print'
import { SanityProductVariantFragment } from '@data/sanity/queries/types/product'
import {
  SanityPrintShopBase,
  SanityShopPrint,
} from '@data/sanity/queries/types/shop'
import { filterDuplicates } from '@lib/helpers'
import { Locale } from '@lib/language'
import { setPrintShopCookie } from './cookie'
import { PrintShopContext } from './print-shop-context'
import { createShop } from './request'
import {
  ShopAction,
  ShopFormStep,
  ShopPrint,
  ShopState,
  SlotConfiguration,
} from './types'

import ReviewStep from '@blocks/shop/form/review-step'
import ShopStep from '@blocks/shop/form/shop-step'
import UploadStep from '@blocks/shop/form/upload-step'

/**
 * Gets disabled status for print slot submit button.
 */
export const getIsPrintSlotSubmitButtonDisabled = (
  configuration: SlotConfiguration,
  imageAreaTypes: SanityPrintAreaType[],
  activePrints: SanityShopPrint[],
  productVariant: SanityProductVariantFragment,
  isCartProductAdding: boolean,
  showAddToCartButton?: boolean,
  permissions?: SanityPrintPermission | null,
) => {
  // Check if variant is out of stock
  if (!productVariant.inStock) {
    return true
  }

  // Check if adding to cart is in progress
  if (showAddToCartButton && isCartProductAdding) {
    return true
  }

  const hasLogoPermission = !!permissions?.logo
  const hasImagePermission = !!permissions?.image
  const hasTextPermission = !!permissions?.text
  const hasLogoArea = imageAreaTypes.includes(SanityPrintAreaType.LOGO)
  const hasImageArea = imageAreaTypes.includes(SanityPrintAreaType.IMAGE)
  const hasTextArea = imageAreaTypes.includes(SanityPrintAreaType.TEXT)
  const hasLogoPrints = activePrints.some((print) => print.type === 'logo')
  const hasImagePrints = activePrints.some((print) => print.type === 'image')
  const showLogos = hasLogoPermission && hasLogoArea && hasLogoPrints
  const showImages = hasImagePermission && hasImageArea && hasImagePrints
  const showText = hasTextPermission && hasTextArea

  const separateLogoImage = !!permissions?.separateLogoImage

  if (showText) {
    // Check if freetext "Yes"/"No" option is not selected or it is "Yes" and freetext is missing
    if (configuration.freetextEnabled === null) {
      return true
    }

    if (
      configuration.freetextEnabled === 'yes' &&
      (!configuration.freetext || configuration.freetext.length === 0)
    ) {
      return true
    }
  }

  if (separateLogoImage) {
    if (showLogos) {
      // Check if logo "Yes"/"No" option is not selected or it is "Yes" and logo is missing
      if (configuration.logoEnabled === null) {
        return true
      }

      if (configuration.logoEnabled === 'yes' && !configuration.logo) {
        return true
      }
    }

    if (showImages) {
      // Check if image "Yes"/"No" option is not selected or it is "Yes" and image is missing
      if (configuration.imageEnabled === null) {
        return true
      }

      if (configuration.imageEnabled === 'yes' && !configuration.image) {
        return true
      }
    }
  }

  if (!separateLogoImage) {
    if (showLogos || showImages) {
      // Check if logo/image "Yes"/"No" option is not selected or it is "Yes" and logo/image is missing
      if (
        (showLogos && configuration.logoEnabled === null) ||
        (showImages && configuration.imageEnabled === null)
      ) {
        return true
      }

      if (
        (showLogos &&
          configuration.logoEnabled === 'yes' &&
          !configuration.logo) ||
        (showImages &&
          configuration.imageEnabled === 'yes' &&
          !configuration.image)
      ) {
        return true
      }
    }
  }

  return false
}

/**
 * Parses Sanity shop prints.
 */
export const parseShopPrints = (prints: SanityShopPrint[]) => {
  const shopPrints = prints
    .map((print) => {
      if (!print.printImage) {
        return
      }

      const printsWithSameImage = prints.filter(
        (subPrint) =>
          subPrint.printImage &&
          print.printImage &&
          subPrint.printImage.id === print.printImage.id,
      )
      const printTypes = printsWithSameImage
        .map((subPrint) => subPrint.type)
        .filter(filterDuplicates)

      const shopPrint: ShopPrint = {
        id: print._key,
        image: print.printImage,
        filename: print.printImage.filename,
        printSlotType: print.slot.type,
        printTypes,
        printColor: print.printColor,
        title: print.title,
        isOptimized: print.isOptimized,
      }

      return shopPrint
    })
    .filter(Boolean) as ShopPrint[]

  // Filter duplicate print asset IDs
  return shopPrints.filter((shopPrint, index, array) => {
    const foundIndex = array.findIndex(
      (item) => item.image.id === shopPrint.image.id,
    )

    return foundIndex === index
  })
}

/**
 * Updates shop state using a shop action.
 */
export const shopStateReducer = (
  state: ShopState,
  action: ShopAction,
): ShopState => {
  const prints = state.prints ?? []

  switch (action.action) {
    case 'updateShopId': {
      return {
        ...state,
        id: action.id,
      }
    }

    case 'updateShopSlug': {
      return {
        ...state,
        slug: action.slug,
      }
    }

    case 'updateShopName': {
      return {
        ...state,
        shopName: action.shopName,
      }
    }

    case 'updateShopEmail': {
      return {
        ...state,
        userEmail: action.email,
      }
    }

    case 'updateInstitutionName': {
      return {
        ...state,
        institutionName: action.institutionName,
      }
    }

    case 'addPrint': {
      return {
        ...state,
        prints: [...prints, action.shopPrint],
      }
    }

    case 'updatePrintTitle': {
      return {
        ...state,
        prints: prints.map((print) => {
          if (print.id !== action.id) {
            return print
          }

          return {
            ...print,
            title: action.title,
          }
        }),
      }
    }

    case 'removePrint': {
      return {
        ...state,
        prints: prints.filter((print) => print.id !== action.id),
      }
    }
  }
}

/**
 * Gets shop form step components and props.
 */
export const getShopFormSteps = (printSlots: SanityPrintSlotSettings[]) => {
  const shopFormSteps: ShopFormStep[] = []
  let currentStepIndex = 0

  printSlots.forEach((printSlot) => {
    shopFormSteps.push({
      component: UploadStep,
      props: {
        stepIndex: currentStepIndex,
        stepType: 'upload',
        printSlot,
      },
    })
    currentStepIndex += 1
  })

  shopFormSteps.push({
    component: ShopStep,
    props: {
      stepIndex: currentStepIndex,
      stepType: 'shop',
    },
  })
  currentStepIndex += 1

  shopFormSteps.push({
    component: ReviewStep,
    props: {
      stepIndex: currentStepIndex,
      stepType: 'review',
    },
  })
  currentStepIndex += 1

  return shopFormSteps
}

/**
 * Hook that sets print shop state in context.
 */
export const useSetPrintShop = (newPrintShop?: SanityPrintShopBase) => {
  const { setPrintShop } = useContext(PrintShopContext)

  useEffect(() => {
    if (newPrintShop) {
      setPrintShop(newPrintShop)
      setPrintShopCookie(newPrintShop)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

/**
 * Gets shop by ID or creates a new shop and returns it.
 */
export const getOrCreateShop = async (
  locale: Locale,
  shopState: ShopState,
  dispatchShopState: (value: ShopAction) => void,
) => {
  // Check if shop exists
  if (shopState?.id) {
    return shopState
  }

  // Create a new shop
  const newShop = await createShop(locale)

  dispatchShopState({
    action: 'updateShopId',
    id: newShop.id,
  })
  dispatchShopState({
    action: 'updateShopSlug',
    slug: newShop.slug,
  })

  const newShopState: ShopState = {
    ...shopState,
    id: newShop.id,
    slug: newShop.slug,
  }

  return newShopState
}
